import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DocumentProvider } from './contexts/DocumentContext';
import { SearchSettingsProvider } from './contexts/SearchSettingsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import MainLayout from './components/layout/MainLayout';
import ExpirationWarning from './components/auth/ExpirationWarning';
import AdminConsolePage from './pages/AdminConsolePage';

const theme = createTheme({
    palette: {
        primary: {
            main: '#4a90e2',
        },
        secondary: {
            main: '#2ecc71',
        },
        background: {
            default: '#f5f6fa',
        },
    },
    typography: {
        fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    },
});

// Inline TokenHandler component
const TokenHandler: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setToken } = useAuth();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        console.log('TokenHandler running. Token:', token);
        if (token) {
            setToken(token);
            navigate('/', { replace: true });
        }
    }, [location, setToken, navigate]);

    return null;
};

const WARNING_HEIGHT = 48; // px, adjust if needed

const AppContent: React.FC = () => {
    const { user } = useAuth();
    const [warningOpen, setWarningOpen] = useState(true);

    const showWarning = Boolean(
        user &&
        user.expirationDate &&
        warningOpen &&
        (() => {
            const expirationDate = new Date(user.expirationDate);
            const now = new Date();
            const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            return daysUntilExpiration <= 7;
        })()
    );

    useEffect(() => {
        setWarningOpen(true);
    }, [user?.id]);

    return (
        <>
            {showWarning && user && (
                <ExpirationWarning user={user} open={warningOpen} onClose={() => setWarningOpen(false)} />
            )}
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify" element={<VerifyEmailPage />} />
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <MainLayout showWarning={showWarning} />
                        </ProtectedRoute>
                    }
                >
                    <Route path="admin" element={<AdminConsolePage />} />
                </Route>
            </Routes>
        </>
    );
};

const App: React.FC = () => (
    <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
            <AuthProvider>
                <Routes>
                    <Route path="/oauth2/callback/*" element={<TokenHandler />} />
                    <Route path="/*" element={
                        <DocumentProvider>
                            <SearchSettingsProvider>
                                <AppContent />
                            </SearchSettingsProvider>
                        </DocumentProvider>
                    } />
                </Routes>
            </AuthProvider>
        </Router>
    </ThemeProvider>
);

export default App;
